import React, { useState } from 'react'
import { trimString } from '../../../../helpers/StringHelper'
import { CaseStudy, CaseStudyContent } from '../../api/Types'
import DocsCaseStudyList from './DocsCaseStudyList'
import DocsQuote from '../common/DocsQuote'
import DocsLogos from '../common/DocsLogos'
import DocsCaseStudySummaryStats, { DEFAULT_SPEC_NAMES } from './DocsCaseStudySummaryStats'
import DocsContact from '../DocsContact'
import Lightbox from '../../../common/Lightbox'
import TalkToExpertButton from '../../../common/TalkToExpertButton'
import Currency, { currencyCb } from '../../../common/Currency'

interface Props {
  case_study: CaseStudyContent
  document_file: string
  document_file_label: string
  more_case_studies: ReadonlyArray<CaseStudy>
}

const DocsCaseStudy: React.FunctionComponent<Props> = ({
  case_study: { header, info, summary, stats, project_overview, project_specs, gallery, quote },
  document_file,
  document_file_label,
  more_case_studies,
}) => {
  const [readMore, setReadMore] = useState(false)

  let overviewCharCount = project_overview?.char_count ? project_overview.char_count : 1210

  const projectSpecsHalf = Math.ceil(project_specs?.length ? project_specs.length / 2 : 0)

  function overviewReadMore() {
    setReadMore(true)
  }

  function displaySpecList(specs) {
    return specs.map(({ label, label_extra, description }, i) => (
      <li key={i}>
        <strong>{label}</strong>
        {label_extra && ` ${label_extra}`}: {description}
      </li>
    ))
  }

  return (
    <>
      <header
        style={{
          backgroundImage: header?.image_url ? `url(${header.image_url})` : '',
        }}
        className='case-study-header'
      >
        <div className='case-study-header-quote'>
          <div className='case-study-header-container'>
            <div className='case-study-header-quote-wrapper'>
              <blockquote className='case-study-header-quote-text'>
                {header?.quote && header.quote}
              </blockquote>
            </div>
          </div>
        </div>
        <div className='case-study-header-title-wrapper'>
          <div className='case-study-header-container'>
            <span className='case-study-header-subtitle'>
              {header?.subtitle && header.subtitle}
            </span>
            <h1 className='case-study-header-title'>{header?.title && header.title}</h1>
          </div>
        </div>
      </header>
      <div className='case-study-info-container'>
        <a href='/customer-stories' className='case-study-back-link'>
          <span className='case-study-back-link-text'>Back to all Customer Stories</span>
        </a>
        <div className='case-study-info-wrapper'>
          <div className='case-study-info'>
            {info?.logo && (
              <img
                className='case-study-info-logo'
                src={info.logo}
                alt={header?.title || 'Case study logo'}
              />
            )}
            {info?.complexity_level && (
              <div className='case-study-info-complexity'>
                Project Complexity
                <span className='case-study-info-complexity-level-text'>
                  {' '}
                  {info.complexity_level} on 4
                </span>
                <span
                  className={`case-study-info-complexity-level case-study-info-complexity-level-${info.complexity_level}`}
                >
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </div>
            )}
            <h2 className='case-study-info-title'>Project Info</h2>
            <ul className='case-study-info-list'>
              {info?.cost && (
                <li>
                  <span className='case-study-info-list-key'>
                    {info.cost_label || 'Equipment Cost'}:
                  </span>{' '}
                  <span className='case-study-info-list-val'>
                    <Currency
                      code={info.cost_currency || 'USD'}
                      currencyCb={currencyCb(info.cost)}
                      autoDetectSymbol={true}
                    />
                  </span>
                </li>
              )}
              {stats?.design_time && (
                <li>
                  <span className='case-study-info-list-key'>
                    {typeof stats.design_time === 'string'
                      ? DEFAULT_SPEC_NAMES.design_time
                      : stats.design_time.name || DEFAULT_SPEC_NAMES.design_time}
                    :
                  </span>{' '}
                  <span className='case-study-info-list-val case-study-info-list-val-time'>
                    {typeof stats.design_time === 'string'
                      ? stats.design_time
                      : stats.design_time.amount}{' '}
                    {typeof stats.design_time !== 'string' && stats.design_time.label
                      ? stats.design_time.label
                      : 'hours'}
                  </span>
                </li>
              )}
              {stats?.assembly_time && (
                <li>
                  <span className='case-study-info-list-key'>
                    {typeof stats.assembly_time === 'string'
                      ? DEFAULT_SPEC_NAMES.assembly_time
                      : stats.assembly_time.name || DEFAULT_SPEC_NAMES.assembly_time}
                    :
                  </span>{' '}
                  <span className='case-study-info-list-val case-study-info-list-val-time'>
                    {typeof stats.assembly_time === 'string'
                      ? stats.assembly_time
                      : stats.assembly_time.amount}{' '}
                    {typeof stats.assembly_time !== 'string' && stats.assembly_time.label
                      ? stats.assembly_time.label
                      : 'hours'}
                  </span>
                </li>
              )}
            </ul>
            {info?.design_id && (
              <a className='case-study-info-design-link' href={`/designs/${info.design_id}`}>
                See the design
              </a>
            )}
          </div>
        </div>
      </div>
      <DocsCaseStudySummaryStats
        summary={summary}
        stats={stats}
        documentFile={document_file}
        documentFileLabel={document_file_label}
      />
      {project_overview && (
        <div className='case-study-overview'>
          <div className='case-study-overview-container'>
            <h2 className='case-study-overview-title'>Project Overview</h2>
            <div className='case-study-overview-content'>
              <h3 className='case-study-overview-content-title'>{project_overview.title}</h3>
              <p>{project_overview.intro}</p>
              {project_overview.content && (
                <div className='case-study-overview-text'>
                  {project_overview.content.map(({ paragraph }, i) => {
                    if (
                      paragraph &&
                      paragraph.length < overviewCharCount &&
                      overviewCharCount > 0
                    ) {
                      overviewCharCount -= paragraph.length
                      return <p key={i}>{paragraph}</p>
                    } else if (
                      paragraph &&
                      paragraph.length >= overviewCharCount &&
                      overviewCharCount >= 0
                    ) {
                      const trimObj = trimString(paragraph, overviewCharCount)
                      const trimmedParagraph = trimObj.trimmedString
                      const restParagraph = paragraph.substring(trimObj.lastIndex)
                      overviewCharCount -= paragraph.length
                      return (
                        <p key={i}>
                          {trimmedParagraph}
                          <span
                            style={{ display: readMore ? 'none' : undefined }}
                            className='case-study-overview-read-more'
                          >
                            <button onClick={overviewReadMore}>Read more</button>
                          </span>
                          <span
                            className={readMore ? undefined : 'case-study-overview-read-more-rest'}
                          >
                            {restParagraph}
                          </span>
                        </p>
                      )
                    } else {
                      return (
                        <p
                          key={i}
                          className={readMore ? undefined : 'case-study-overview-read-more-rest'}
                        >
                          {paragraph}
                        </p>
                      )
                    }
                  })}
                </div>
              )}
              <div className='case-study-overview-image'>
                <img
                  src={project_overview.image_url}
                  alt={project_overview.title || 'project overview'}
                  loading='lazy'
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {project_specs && (
        <div className='case-study-specs'>
          <div className='case-study-specs-wrapper'>
            <h2 className='case-study-specs-title'>Project Specs</h2>
            <div className='case-study-specs-list-wrapper'>
              <ul className='case-study-specs-list'>
                {displaySpecList(project_specs.slice(0, projectSpecsHalf))}
              </ul>
              <ul className='case-study-specs-list'>
                {displaySpecList(project_specs.slice(projectSpecsHalf))}
              </ul>
            </div>
          </div>

          <div className='case-study-specs-cta-wrapper'>
            {info?.design_id ? (
              <a className='case-study-specs-design-link' href={`/designs/${info.design_id}`}>
                See the design
              </a>
            ) : (
              <TalkToExpertButton
                className='case-study-specs-cta'
                trackingKey='book_meeting_case_study_specs'
                noIcon={true}
                text='Book a demo'
              />
            )}
          </div>
        </div>
      )}
      {gallery && (
        <div className='case-study-gallery'>
          <div className='case-study-gallery-container'>
            <h2 className='case-study-gallery-title'>Gallery</h2>
            <div className='case-study-gallery-images'>
              <ul className='case-study-gallery-images-list'>
                <Lightbox
                  imageUrls={gallery.reduce((images: string[], image) => {
                    if (image.image_url !== undefined) {
                      images.push(image.image_url)
                    }
                    return images
                  }, [])}
                  renderChildren={onClick =>
                    gallery.map(({ thumbnail_image_url }, i) => (
                      <li key={`case-study-gallery-${i + 1}`}>
                        <img
                          className='lightbox-img-portal'
                          onClick={() => onClick(i)}
                          src={thumbnail_image_url}
                          alt={`Gallery image: ${i}`}
                          loading='lazy'
                        />
                      </li>
                    ))
                  }
                />
              </ul>
            </div>
          </div>
        </div>
      )}
      {quote && <DocsQuote tag='h2' text={quote.text} author={quote.author} logo={quote.logo} />}
      <DocsLogos />
      <div className='case-study-more'>
        <div className='case-study-more-container'>
          <h2 className='case-study-more-title'>More case studies</h2>
          <DocsCaseStudyList case_studies={more_case_studies} />
        </div>
      </div>
      <DocsContact />
    </>
  )
}

export default DocsCaseStudy
